// Pantalla de planta editable: entrá con un PDF, salí con áreas.
//
// La entrada es un croquis, no un registro de la base. Eso es a propósito: el
// que vende un sábado tiene el PDF del modelo a mano, no el IDMod.
//
// Cualquier croquis abre. Si el nombre está en el catálogo ya medido
// (`components/planta/medidos/`, que publica `planta-tool/publicar.py`) se abre
// al instante y sin red; si no, el PDF va al convertidor
// (`POST /api/planta/convertir`) y vuelve medido. `fuente.ts` decide cuál, y ni
// el editor ni la medición se enteran de la diferencia.
//
// Lo único que no abre es la lámina que el convertidor NO puede leer con
// confianza: esas vuelven 400 con el motivo, y el motivo se muestra tal cual.
// Mejor «esta lámina no tiene cotas» que un editor que abre una casa que no es.
//
// La segunda entrada es el DROPDOWN DE MODELO, y existe porque la primera
// dejaba al catálogo sin plano de fondo: el croquis de cada modelo ya está en
// `Modelos.AzureBlobCroquisBN`, pero el navegador no lo puede bajar —el blob
// tiene lista blanca de CORS sin `arquiflow.adelante.cr`, así que pdfjs se come
// el error aunque `window.open` abra el PDF sin chistar—. Elegir el modelo baja
// sus bytes por `GET /api/planta/croquis/{id}`, que es mismo-origen, y de ahí
// sale tanto el fondo como —si nadie lo midió todavía— las paredes.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardText, PencilSimple, SpinnerGap, Upload, Cube,
} from '@phosphor-icons/react';
import { api, type ApiError } from '@/api/client';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { AdminObjetos } from '@/components/planta/AdminObjetos';
import { EditorPlanta } from '@/components/planta/EditorPlanta';
import {
  cargarPlanta, cargarPlantaDeArchivo, cargarPlantaDeBytes, tienePlanta,
} from '@/components/planta/fuente';
import { MEDIDOS } from '@/components/planta/medidos/indice';
import { desdeImportador, enBlanco, type ModeloPlanta } from '@/components/planta/tipos';
import { useAppStore } from '@/store/appStore';

/** Lo que el plano trae rotulado como su propia área cerrada, por planta. */
function anclasDelPlano(crudo: Parameters<typeof desdeImportador>[0]) {
  const out: Record<number, number> = {};
  for (const pl of crudo.plantas) {
    const t = Math.max(0, ...(pl.totales ?? []).map((x) => x.area));
    if (t) out[pl.n] = t;
  }
  return out;
}

const bonito = (pdf: string) => pdf.replace(/\.pdf$/i, '').replace(/^CroquisBN-?/i, '');

/**
 * Un PDF escondido en un texto, o null.
 *
 * `JVBERi0` no es una constante mágica: es `%PDF-` escrito en base64, los
 * primeros bytes que todo PDF tiene por norma. Chequearlo ANTES de decodificar
 * evita tragarse cualquier texto largo que ande en el portapapeles —una
 * conversación de WhatsApp, un link— y presentarlo como un croquis roto.
 *
 * Acepta las dos formas en que un Atajo de iOS puede dejarlo: el base64 pelado
 * y el `data:` completo. Por eso se corta el prefijo antes de mirar la firma.
 */
function pdfDeTexto(texto: string | undefined): File | null {
  const t = (texto ?? '').trim().replace(/\s+/g, '');
  if (!t) return null;
  const b64 = t.startsWith('data:') ? t.slice(t.indexOf(',') + 1) : t;
  if (!b64.startsWith('JVBERi0')) return null;
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], 'Pegado.pdf', { type: 'application/pdf' });
  } catch {
    return null;
  }
}

export function PlantaPage() {
  const [abierto, setAbierto] = useState<{ modelo: ModeloPlanta; url: string | null; nombre: string } | null>(null);
  // El catálogo de objetos, que se administra SIN abrir un plano. Va como
  // estado y no como ruta propia por lo mismo que el editor: esta pantalla ya
  // sabe tomar el alto completo, y una ruta más habría que declararla —y
  // mantenerla— en los dos repos, que tienen routers distintos.
  const [objetos, setObjetos] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // Nombre del croquis que está en el convertidor, o null. Medir una lámina es
  // ~1 s, pero el arranque en frío del servicio se come varios: sin esto la
  // pantalla parece colgada y el que vende suelta el archivo de nuevo.
  const [midiendo, setMidiendo] = useState<string | null>(null);
  const archivoRef = useRef<HTMLInputElement | null>(null);
  // `?pegar=1` lo pone el Atajo de iOS, no un humano: es la marca de que esta
  // pantalla se abrió con un croquis esperando en el portapapeles.
  const [llegaDeWhatsApp] = useState(
    () => new URLSearchParams(window.location.search).get('pegar') === '1',
  );

  // Con el editor abierto, el shell de ArquiFlow deja de pintar sus dos franjas
  // (pedido de Daniel del 19-oct-2026). La limpieza del `return` no es opcional:
  // sin ella, salir de /planta con el editor abierto —un deep link, el botón de
  // atrás— deja la app entera sin navegación.
  const setPantallaCompleta = useAppStore((s) => s.setPantallaCompleta);
  useEffect(() => {
    setPantallaCompleta(abierto != null);
    return () => setPantallaCompleta(false);
  }, [abierto, setPantallaCompleta]);

  // El catálogo del dropdown. `staleTime` largo porque los modelos del blob no
  // cambian en una sesión, y si falla la red queda el catálogo medido, que es
  // local: la pantalla no se cae, se angosta.
  const { data: catalogo = [], isLoading: cargandoCatalogo } = useQuery({
    queryKey: ['planta', 'modelos'],
    queryFn: ({ signal }) => api.planta.modelos(signal),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Un solo selector con las dos procedencias, no dos listas.
  //
  // Es que ninguna de las dos cubre a la otra: hay modelos del catálogo de la
  // base que nadie midió todavía (van al convertidor) y hay 11 croquis medidos
  // cuyo modelo ya no está activo en la base (medido el 3-oct-2026: 32 medidos
  // contra 83 croquis de activos, 21 en común). Dejar sólo el dropdown de la
  // base perdería esos 11; dejar sólo los medidos es lo que ya había.
  const opciones = useMemo(() => {
    // Hay nombres repetidos en el catálogo (dos «Caspe T 4D 2B» distintos, con
    // croquis distinto). Dos filas idénticas en la lista son una trampa: el que
    // vende elige una y no sabe cuál eligió. Cuando el nombre se repite se le
    // agrega el archivo, que es lo que de verdad las separa.
    const veces = new Map<string, number>();
    for (const m of catalogo) {
      const n = m.nombre?.trim() || `Modelo ${m.idModelo}`;
      veces.set(n, (veces.get(n) ?? 0) + 1);
    }

    const deBase = catalogo.map((m) => {
      const nombre = m.nombre?.trim() || `Modelo ${m.idModelo}`;
      const medido = tienePlanta(m.croquis ?? null);
      const estado = medido
        ? 'Con plano de fondo · paredes ya medidas'
        : 'Con plano de fondo · se manda a medir al abrir';
      return {
        value: `m:${m.idModelo}`,
        label: nombre,
        sublabel: (veces.get(nombre) ?? 0) > 1 && m.croquis
          ? `${estado} · ${bonito(m.croquis)}`
          : estado,
        idModelo: m.idModelo,
        croquis: m.croquis ?? '',
      };
    });

    // Los medidos que ningún modelo activo reclama. Abren igual, pero sin
    // fondo: de ellos sólo tenemos las paredes, no de dónde bajar la lámina.
    const reclamados = new Set(
      catalogo.map((m) => m.croquis).filter(Boolean) as string[],
    );
    const huerfanos = Object.keys(MEDIDOS)
      .filter((pdf) => !reclamados.has(pdf))
      .sort()
      .map((pdf) => ({
        value: `c:${pdf}`,
        label: bonito(pdf),
        sublabel: 'Sin plano de fondo · paredes ya medidas',
        idModelo: null as number | null,
        croquis: pdf,
      }));

    return [...deBase, ...huerfanos];
  }, [catalogo]);

  /** Abrir eligiendo del dropdown. */
  async function elegir(value: string | null) {
    if (!value) return;
    const op = opciones.find((o) => o.value === value);
    if (!op) return;
    setAviso(null);

    // Sin idModelo no hay de dónde bajar el PDF: es un croquis medido cuyo
    // modelo ya no está activo. Abre como siempre, sin fondo.
    if (op.idModelo == null) {
      await abrir(op.croquis, null, op.croquis);
      return;
    }

    setMidiendo(op.label);
    let url: string | null = null;
    try {
      const pdf = await api.planta.croquis(op.idModelo);
      url = URL.createObjectURL(pdf);
      const crudo = await cargarPlantaDeBytes(pdf, op.croquis);
      setAbierto({ modelo: desdeImportador(crudo, anclasDelPlano(crudo)), url, nombre: op.label });
    } catch (e) {
      if (url) URL.revokeObjectURL(url);
      const err = e as ApiError;
      // Si las paredes ya estaban medidas, que falle la bajada del PDF no es
      // motivo para no abrir: se pierde el fondo, no la casa. Vale la pena
      // decirlo y abrir igual, porque es exactamente el sábado del que vende.
      if (tienePlanta(op.croquis)) {
        setAviso(`No se pudo traer el plano de «${op.label}» (${err.message}).`
          + ` Se abre con las paredes medidas, pero sin el PDF de fondo.`);
        await abrir(op.croquis, null, op.label);
        return;
      }
      setAviso(err.status === 400
        ? `«${op.label}» no se pudo medir: ${err.message}.`
        : `No se pudo abrir «${op.label}»: ${err.message}.`);
    } finally {
      setMidiendo(null);
    }
  }

  async function abrir(clave: string, url: string | null, nombre: string) {
    setAviso(null);
    const crudo = await cargarPlanta(clave);
    if (!crudo) {
      setAviso(`«${nombre}» todavía no tiene las paredes medidas. Se puede abrir el`
        + ` plano, pero no hay áreas vivas hasta que el convertidor lea este croquis.`);
      return;
    }
    setAbierto({ modelo: desdeImportador(crudo, anclasDelPlano(crudo)), url, nombre });
  }

  async function recibir(f: File | null | undefined) {
    if (!f) return;
    setAviso(null);
    setMidiendo(f.name);
    // El objectURL es lo que ve pdfjs de fondo, siempre local. Los bytes sólo
    // viajan al convertidor cuando el croquis no está en el catálogo medido.
    const url = URL.createObjectURL(f);
    try {
      const crudo = await cargarPlantaDeArchivo(f);
      setAbierto({ modelo: desdeImportador(crudo, anclasDelPlano(crudo)), url, nombre: f.name });
    } catch (e) {
      URL.revokeObjectURL(url);
      const err = e as ApiError;
      // El 400 del convertidor ya trae el motivo escrito para leerse (sin
      // cadenas de cota, cotas que no concuerdan, sin masas con forma de
      // pared): se muestra tal cual. Cualquier otro status es del servicio, no
      // del archivo, y conviene decirlo distinto para no mandar a nadie a
      // buscarle defectos a un croquis que está bien.
      setAviso(err.status === 400
        ? `«${f.name}» no se pudo medir: ${err.message}.`
        : `El convertidor no respondió (${err.message}). El plano se puede abrir,`
          + ` pero las paredes vivas necesitan el servicio arriba.`);
    } finally {
      setMidiendo(null);
    }
  }

  /**
   * PEGAR EL PDF DEL PORTAPAPELES.
   *
   * El flujo real que lo pide: el croquis llega por WhatsApp, se abre, se copia.
   * Hasta hoy había que bajarlo a Archivos y volver a buscarlo acá.
   *
   * No hay forma de ADIVINAR que hay un PDF esperando: ningún navegador deja
   * espiar el portapapeles de fondo —sería un agujero de privacidad— y la
   * lectura exige un gesto del usuario, que en Safari iOS además pinta su propio
   * cartel de «Pegar» para confirmar. Por eso esto es un BOTÓN y no un aviso que
   * aparece solo: el toque ES el permiso.
   *
   * Y el portapapeles no trae nombre de archivo, sólo bytes. Eso tiene una
   * consecuencia que se nota: un croquis pegado NUNCA va a coincidir con el
   * catálogo ya medido —que se busca por nombre— así que siempre pasa por el
   * convertidor. Son unos segundos, no un error.
   *
   * DOS PUERTAS, Y LA SEGUNDA ES LA DEL IPHONE (21-oct-2026)
   *
   * `clipboard.read()` entrega el PDF tal cual en la compu. En Safari iOS NO:
   * su portapapeles asincrónico sólo entrega un puñado de tipos —texto, HTML,
   * PNG, URL— y `application/pdf` no está en la lista. Por eso el botón que ya
   * existía, que es exactamente el camino que pide Daniel («que se copie un PDF
   * desde un grupo de WhatsApp y se pueda pegar»), no podía funcionar en el
   * teléfono por más que el PDF estuviera copiado.
   *
   * La segunda puerta rodea eso sin pelearse con Safari: el PDF llega como
   * TEXTO en base64, que sí entrega. Quien lo deja ahí es un Atajo de iOS en la
   * hoja de compartir de WhatsApp (ver `docs/atajo-ios-croquis.md`), así que el
   * camino completo pasa de «guardar en Archivos y volver a buscarlo» a
   * compartir → Atajo → pegar.
   */
  async function pegar() {
    if (midiendo) return;
    setAviso(null);
    if (!navigator.clipboard?.read && !navigator.clipboard?.readText) {
      setAviso('Este navegador no deja leer el portapapeles. Soltá el PDF acá arriba'
        + ' o elegilo del catálogo.');
      return;
    }
    try {
      // Puerta 1 — el PDF como archivo. Es la de la compu.
      for (const it of (await navigator.clipboard.read?.()) ?? []) {
        const tipo = it.types.find((t) => t === 'application/pdf' || t.endsWith('/pdf'));
        if (!tipo) continue;
        const blob = await it.getType(tipo);
        await recibir(new File([blob], 'Pegado.pdf', { type: 'application/pdf' }));
        return;
      }
      // Puerta 2 — el PDF como texto en base64. Es la del iPhone.
      const f = pdfDeTexto(await navigator.clipboard.readText?.());
      if (f) { await recibir(f); return; }

      setAviso('No encontré un PDF en el portapapeles. Abrí el croquis, tocá Copiar y'
        + ' volvé a intentarlo.');
    } catch (e) {
      // Negar el permiso del cartel de Safari entra por acá, igual que un
      // portapapeles que el sistema no quiere entregar. No es culpa del PDF.
      setAviso(`No se pudo leer el portapapeles (${(e as Error).message}).`);
    }
  }

  // Ctrl+V / ⌘V en la compu. Va aparte del botón porque es OTRA puerta: el
  // evento `paste` sí entrega el archivo con su nombre —y por ahí el croquis
  // todavía puede caer en el catálogo medido—, mientras que el botón sólo
  // consigue bytes. Con el editor abierto no se escucha: ahí el pegado es suyo.
  useEffect(() => {
    if (abierto) return;
    function alPegar(e: ClipboardEvent) {
      if (midiendo) return;
      const f = Array.from(e.clipboardData?.files ?? [])
        .find((x) => x.type === 'application/pdf' || /\.pdf$/i.test(x.name))
        ?? pdfDeTexto(e.clipboardData?.getData('text'));
      if (!f) return;
      e.preventDefault();
      void recibir(f);
    }
    window.addEventListener('paste', alPegar);
    return () => window.removeEventListener('paste', alPegar);
    // `recibir` se redefine en cada repintado; lo que importa es que el oyente
    // vea el `midiendo` de ahora, y eso ya lo garantiza la dependencia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, midiendo]);

  if (objetos) {
    return (
      <div className="absolute inset-0">
        <AdminObjetos onCerrar={() => setObjetos(false)} />
      </div>
    );
  }

  if (abierto) {
    return (
      // La cabecera —X, nombre del modelo, guardar, exportar— la dibuja el
      // EDITOR desde el 6-oct-2026. Acá había una propia y era la tercera copia
      // de la misma fila, contando la de AdVentas.
      <div className="absolute inset-0 flex flex-col bg-brand-bg">
        <EditorPlanta
          planoUrl={abierto.url}
          modelo={abierto.modelo}
          titulo={bonito(abierto.nombre)}
          onCerrar={() => {
            if (abierto.url) URL.revokeObjectURL(abierto.url);
            setAbierto(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl">
        {/* El catálogo de objetos entra ACÁ y no entre los tres botones de
            abajo: esos tres contestan «de dónde sale la planta» y éste no es
            una cuarta respuesta a esa pregunta, es otra tarea. Metido en esa
            fila haría pensar que abre un plano. */}
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-bold text-brand-gray-500">Planta editable</h1>
          <button
            type="button"
            onClick={() => setObjetos(true)}
            data-abrir-objetos=""
            className="flex shrink-0 items-center gap-1.5 rounded-xl border
                       border-stone-300 bg-white px-3 py-2 text-sm font-semibold
                       text-brand-gray-500 hover:border-brand-green-100"
          >
            <Cube size={16} weight="bold" className="text-brand-gray-400" />
            Objetos
          </button>
        </div>
        <p className="mt-1 text-sm text-brand-gray-400">
          Soltá el croquis del modelo. Las paredes aparecen encima del plano y
          podés moverlas con el dedo: el mobiliario y las cotas quedan debajo, y
          el área se recalcula cuando lo pedís.
        </p>

        {/* LA LLEGADA DESDE WHATSAPP.
            El Atajo de iOS deja el croquis en el portapapeles y abre esta
            pantalla con `?pegar=1`. Acá no se puede pegar solo: Safari exige un
            toque para entregar el portapapeles, y encima pinta su propio cartel
            de confirmación. Lo único que se puede hacer —y es lo que hace esta
            tarjeta— es que ese toque sea el PRIMERO que uno ve al llegar, en
            vez de un botón más abajo entre otros tres. */}
        {llegaDeWhatsApp && !abierto && (
          <button
            type="button"
            disabled={midiendo != null}
            onClick={() => void pegar()}
            data-pegar-grande=""
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl
                       bg-brand-green-100 px-4 py-5 text-base font-bold text-black
                       shadow-md hover:bg-brand-green-200 disabled:opacity-50"
          >
            <ClipboardText size={22} weight="bold" />
            Pegar el croquis de WhatsApp
          </button>
        )}

        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!midiendo) void recibir(e.dataTransfer.files?.[0]);
          }}
          className={`mt-4 flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center ${
            midiendo
              ? 'cursor-wait border-brand-green-100 bg-white'
              : 'cursor-pointer border-stone-300 bg-white hover:border-brand-green-100'
          }`}
        >
          {midiendo ? (
            <>
              <SpinnerGap size={28} weight="bold" className="animate-spin text-brand-green-200" />
              <span className="text-sm font-semibold text-brand-gray-500">
                Midiendo «{bonito(midiendo)}»…
              </span>
              <span className="text-xs text-brand-gray-400">
                Leer un croquis es cosa de segundos; la primera vez del día, algo más.
              </span>
            </>
          ) : (
            <>
              <Upload size={28} weight="bold" className="text-brand-gray-400" />
              <span className="text-sm font-semibold text-brand-gray-500">
                Arrastrá el PDF del croquis o tocá para elegirlo
              </span>
              <span className="text-xs text-brand-gray-400">
                Los modelos ya medidos se leen en el navegador; el resto se manda a
                medir y vuelve con sus paredes.
              </span>
            </>
          )}
          <input
            ref={archivoRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            disabled={midiendo != null}
            onChange={(e) => void recibir(e.target.files?.[0])}
          />
        </label>

        {/* PEGAR y ARRANCAR EN BLANCO: las otras dos respuestas a la misma
            pregunta —de dónde sale la planta—. Van pegadas a la zona de soltar y
            no en el catálogo para que las tres estén a la vista de entrada.

            Pegar va PRIMERO porque es el atajo del croquis que llega por
            WhatsApp, que es el camino de todos los días. */}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            disabled={midiendo != null}
            onClick={() => void pegar()}
            className="flex items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm font-semibold text-brand-gray-500 hover:border-brand-green-100 disabled:opacity-50"
          >
            <ClipboardText size={16} weight="bold" className="text-brand-gray-400" />
            Pegar el PDF copiado
          </button>
          <button
            type="button"
            disabled={midiendo != null}
            onClick={() => setAbierto({ modelo: enBlanco(), url: null, nombre: 'Planta nueva' })}
            className="flex items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm font-semibold text-brand-gray-500 hover:border-brand-green-100 disabled:opacity-50"
          >
            <PencilSimple size={16} weight="bold" className="text-brand-gray-400" />
            …o arrancá en blanco
          </button>
        </div>

        {aviso && (
          <p className="mt-3 rounded-lg border border-brand-yellow bg-brand-yellow/10 px-3 py-2 text-xs text-brand-gray-500">
            {aviso}
          </p>
        )}

        <h2 className="mt-8 text-sm font-bold text-brand-gray-500">
          …o elegí el modelo del catálogo
          <span className="ml-2 font-normal text-brand-gray-400">{opciones.length}</span>
        </h2>
        <p className="mt-1 text-xs text-brand-gray-400">
          El croquis lo trae el servidor, así que estos abren con el plano de
          fondo sin tener el PDF a mano.
        </p>
        <div className="mt-3 max-w-md">
          <SearchableSelect
            options={opciones}
            value={null}
            onChange={(v) => void elegir(v)}
            disabled={midiendo != null}
            clearable={false}
            placeholder={cargandoCatalogo ? 'Cargando modelos…' : '— Elegí un modelo —'}
            emptyText="Ningún modelo con ese nombre"
          />
        </div>
      </div>
    </div>
  );
}
